// WAP Phase 2 (S4.1) — applies the farmer's organization branding at runtime.
// Mounted once inside AuthProvider. Renders nothing.
//
// Boot: apply any cached branding immediately (no flash of the generic theme for a
// returning branded farmer). On auth: fetch the live branding (session-bound edge fn)
// and apply/refresh. On logout: clear → the static :root default (generic CCS) returns.
// Branding is additive and best-effort: any failure leaves the default intact.
import { useEffect, useRef } from "react";
import { useAuth } from "@/features/auth/components/AuthContext";
import { useBrandingStore } from "@/stores/brandingStore";
import { applyBranding, clearBranding } from "@/lib/branding";
import { getOrgBranding } from "@/shared/api/edgeFunctions";

export function OrgBrandingApplier(): null {
  const { user, isLoading } = useAuth();
  const bootApplied = useRef(false);

  // 1) Instant apply of cached branding on first mount (avoids FOUC for returning users).
  useEffect(() => {
    if (bootApplied.current) return;
    bootApplied.current = true;
    const cached = useBrandingStore.getState().branding;
    if (cached) applyBranding(cached);
  }, []);

  // 2) On auth state, refresh from the live (session-bound) endpoint.
  useEffect(() => {
    if (isLoading) return;
    let cancelled = false;

    if (!user?.id) {
      // Logged out → drop any brand and clear cache so the next farmer starts clean.
      clearBranding();
      useBrandingStore.getState().clear();
      return;
    }

    (async () => {
      try {
        const res = await getOrgBranding();
        if (cancelled) return;
        applyBranding(res.branding);
        useBrandingStore.getState().setBranding(res.organization_id, res.branding);
      } catch {
        // Best-effort: keep whatever is applied (cached or default); never block the farmer.
      }
    })();

    return () => { cancelled = true; };
  }, [user?.id, isLoading]);

  return null;
}
